import { Alert, Card, Flex, Form, Input } from 'antd'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useCounterpartServiceGetInternalInstitutionEndpoint,
  useInstitutionServiceGetInstitutionEndpoint,
  useTreatmentServiceCreateEpidemiologyCaseEndpoint
} from 'src/api/queries'
import { ApiError, PersonIdentityStatus } from 'src/api/requests'
import { FormFooter, message, PageBreadcrumbs } from 'src/components'
import { usePersonBreadcrumb } from 'src/features/persons/hooks'
import { useUser } from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formatDates, formErrorHandler } from 'src/utils'

import {
  EpidemiologyFinalDiagnosticCard,
  EpidemiologyNotificationCard,
  EpidemiologyPatientInformationCard,
  EpidemiologyPrimaryDiagnosticCard,
  EpidemiologyRegistrationCard,
  EpidemiologySymptomsCard
} from '../components'
import { useLatestConsultationDiagnosesPrefill } from '../useLatestConsultationDiagnosesPrefill'
import { useRegistrationPrefill } from '../useRegistrationPrefill'

const toIds = (items?: Array<{ id?: string }> | null) =>
  items?.map((item) => item.id).filter((id): id is string => !!id) ?? []

export const AddEpidemiologyCasePage = () => {
  const { t } = useLocales()
  const navigate = useNavigate()

  const { personId } = useParams()
  const {
    breadcrumbItems: items,
    isLoadingPerson,
    person
  } = usePersonBreadcrumb(personId)

  const { userData } = useUser()
  const currentLegalEntityId = userData?.currentLegalEntityId

  const { data: internalInstitution, isLoading: isLoadingInternalInstitution } =
    useCounterpartServiceGetInternalInstitutionEndpoint(
      { id: String(currentLegalEntityId) },
      [currentLegalEntityId],
      { enabled: !!currentLegalEntityId }
    )

  const institutionId = internalInstitution?.institutionId

  const {
    data: currentInstitution,
    isLoading: isLoadingInstitution,
    isError: isInstitutionError
  } = useInstitutionServiceGetInstitutionEndpoint(
    { id: String(institutionId) },
    [institutionId],
    { enabled: !!institutionId, retry: false }
  )

  const [form] = Form.useForm()

  useLatestConsultationDiagnosesPrefill(form, personId, true, person, {
    name: internalInstitution?.name,
    code: internalInstitution?.idno
  })

  useRegistrationPrefill(form, userData)

  useEffect(() => {
    const fullName = [userData?.firstName, userData?.lastName]
      .filter(Boolean)
      .join(' ')
    if (!fullName) return
    form.setFieldValue(['form', 'notification', 'notifierFullName'], fullName)
  }, [userData?.firstName, userData?.lastName])

  useEffect(() => {
    if (!currentInstitution) return

    form.setFieldsValue({
      form: {
        notification: {
          institution: {
            id: currentInstitution.id,
            title: currentInstitution.name,
            code: currentInstitution.institutionCode,
            medicalAssistanceType:
              currentInstitution.medicalAssistanceTypes?.[0],
            classification: currentInstitution.institutionClassificationIds?.[0]
              ? { id: currentInstitution.institutionClassificationIds[0] }
              : undefined,
            address: {
              country: currentInstitution.address?.country,
              locality: currentInstitution.address?.locality,
              cuatmLocalityId: currentInstitution.address?.cuatmLocality?.id,
              street: currentInstitution.address?.street,
              streetNumber: currentInstitution.address?.unitNumber
            },
            phoneNumber: currentInstitution.contactInfo?.phone
          }
        },
        registration: {
          institutionTitle: currentInstitution.name
        }
      }
    })
  }, [currentInstitution])

  useEffect(() => {
    if (currentInstitution || !internalInstitution) return

    form.setFieldsValue({
      form: {
        notification: {
          institution: {
            id: internalInstitution.institutionId,
            title: internalInstitution.name,
            address: {
              country: internalInstitution.address?.country,
              locality: internalInstitution.address?.locality,
              cuatmLocalityId: internalInstitution.address?.cuatmLocality?.id,
              street: internalInstitution.address?.street,
              streetNumber: internalInstitution.address?.unitNumber
            },
            phoneNumber: internalInstitution.contactInfo?.phone
          }
        },
        registration: {
          institutionTitle: internalInstitution.name
        }
      }
    })
  }, [internalInstitution, currentInstitution])

  useEffect(() => {
    if (!person) return

    form.setFieldsValue({
      form: {
        patient: {
          identityStatus: person.isAnonymous
            ? PersonIdentityStatus.ANONYMOUS
            : PersonIdentityStatus.IDENTIFIED,
          firstName: person.firstName,
          lastName: person.lastName,
          idnp: person.idnp,
          birthDate: person.birthDate ? dayjs(person.birthDate) : undefined,
          gender: person.gender,
          citizenship: person.citizenship,
          currentAddress: {
            country: person.currentAddress?.country,
            street: person.currentAddress?.street,
            streetNumber: person.currentAddress?.streetNumber,
            apartmentNumber: person.currentAddress?.apartmentNumber,
            cuatmLocality: person?.currentAddress?.cuatmLocality?.id
              ? {
                  id: person.currentAddress.cuatmLocality.id,
                  name: person.currentAddress.cuatmLocality.translation
                }
              : undefined
          },
          residenceAddress: {
            country: person.residenceAddress?.country,
            street: person.residenceAddress?.street,
            streetNumber: person.residenceAddress?.streetNumber,
            apartmentNumber: person.residenceAddress?.apartmentNumber,
            cuatmLocality: person?.residenceAddress?.cuatmLocality?.id
              ? {
                  id: person.residenceAddress.cuatmLocality.id,
                  name: person.residenceAddress.cuatmLocality.translation
                }
              : undefined
          }
        }
      }
    })
  }, [person])

  const { mutate, isPending } =
    useTreatmentServiceCreateEpidemiologyCaseEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: (response) => {
        message.success(t('epidemiology.success_create'))
        if (response?.id) {
          navigate(
            paths.patients.epidemiologyCases.details(
              String(personId),
              response.id
            )
          )
        } else {
          navigate(paths.patients.details(String(personId)))
        }
      }
    })

  const breadcrumbItems = [
    ...items,
    { title: t('epidemiology.add_epidemiology_case') }
  ]

  const onSave = async () => {
    await form.validateFields()
    const values = form.getFieldsValue(true)

    const formValues = values.form ?? {}
    const primary = formValues.primaryDiagnostic ?? {}
    const final = formValues.finalDiagnostic ?? {}

    const preliminaryDiagnosticIds = toIds(primary.diagnoses)
    const finalDiagnosticIds = toIds(final.diagnoses)

    const institutionPayload = currentInstitution
      ? {
          id: currentInstitution.id,
          title: currentInstitution.name,
          code: currentInstitution.institutionCode,
          medicalAssistanceType: currentInstitution.medicalAssistanceTypes?.[0],
          classification: currentInstitution.institutionClassificationIds?.[0]
            ? { id: currentInstitution.institutionClassificationIds[0] }
            : undefined,
          address: {
            country: currentInstitution.address?.country,
            locality: currentInstitution.address?.locality,
            cuatmLocalityId: currentInstitution.address?.cuatmLocality?.id,
            street: currentInstitution.address?.street,
            streetNumber: currentInstitution.address?.unitNumber
          },
          phoneNumber: currentInstitution.contactInfo?.phone
        }
      : formValues.notification?.institution

    const patient = formValues.patient ?? {}

    const formPayload = formatDates({
      ...formValues,
      notification: {
        ...(formValues.notification ?? {}),
        institution: institutionPayload
      },
      patient: {
        ...patient,
        socialStatus: person?.socialStatus,
        currentAddress: patient.currentAddress,
        residenceAddress: patient.residenceAddress
      }
    })

    mutate({
      requestBody: {
        personId: String(personId),
        preliminaryDiagnosticIds,
        finalDiagnosticIds,
        dateOfDiseaseOnset: primary.dateOfDiseaseOnset
          ? dayjs(primary.dateOfDiseaseOnset).format(yearMonthDaySlash)
          : null,
        diagnosisDate: final.diagnosisDate
          ? dayjs(final.diagnosisDate).format(yearMonthDaySlash)
          : null,
        notes: values.notes ?? null,
        form: formPayload as typeof formValues
      }
    })
  }

  return (
    <PageBreadcrumbs
      title={t('epidemiology.add_epidemiology_case')}
      items={breadcrumbItems}
      showBack
    >
      {isInstitutionError ? (
        <Alert
          type="warning"
          showIcon
          message={t('epidemiology.no_institution_access_title')}
          description={t('epidemiology.no_institution_access_description')}
        />
      ) : (
        <Form layout="vertical" requiredMark={false} form={form}>
          <Flex vertical gap={24}>
            <EpidemiologyNotificationCard />
            <EpidemiologyPrimaryDiagnosticCard form={form} />
            <EpidemiologyPatientInformationCard form={form} person={person} />
            <EpidemiologyRegistrationCard />
            <EpidemiologySymptomsCard form={form} />
            <EpidemiologyFinalDiagnosticCard form={form} />

            <Card>
              <Form.Item
                name="notes"
                label={t('form.notes')}
                rules={[{ max: 1000 }]}
              >
                <Input.TextArea rows={4} placeholder={t('form.notes')} />
              </Form.Item>
            </Card>
          </Flex>

          <FormFooter
            onCancel={() => navigate(-1)}
            onSave={onSave}
            saveConfirmTitle={`${t('epidemiology.save')}?`}
            saveLabel={t('epidemiology.save')}
            loading={
              isPending ||
              isLoadingPerson ||
              isLoadingInternalInstitution ||
              isLoadingInstitution
            }
          />
        </Form>
      )}
    </PageBreadcrumbs>
  )
}
